import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Tv, Play, RefreshCw, Download, Calendar, Eye, EyeOff } from 'lucide-react'
import api from '../api/client'
import { videos } from '../api/client'
import { useAuthStore } from '../stores/auth'
import { useVideoPlayerStore } from '../stores/videoPlayer'
import VideoMenu from '../components/VideoMenu'
import type { EpisodeInfo } from '../types'

interface Episode {
  id: string
  title: string
  video_type: string
  year: number | null
  duration_ms: number | null
  file_path: string
  resolution: string | null
  codec: string | null
  container: string | null
  library_id: string
  series_id: string | null
  season_number: number | null
  episode_number: number | null
  overview: string | null
  thumbnail_path: string | null
}

interface SeriesDetail {
  id: string
  title: string
  year: number | null
  overview: string | null
  poster_path: string | null
  library_id: string
  episodes: Episode[]
  tvmaze_id: number | null
  all_episodes: EpisodeInfo[]
}

function formatDuration(ms: number | null): string {
  if (!ms) return ''
  const minutes = Math.round(ms / 60000)
  return `${minutes} min`
}

export default function Series() {
  const { id } = useParams<{ id: string }>()
  const [activeSeason, setActiveSeason] = useState<number | null>(null)
  const [watchedStatus, setWatchedStatus] = useState<Record<string, boolean>>({})
  const [showOnlyDownloaded, setShowOnlyDownloaded] = useState(false)
  const queryClient = useQueryClient()
  const token = useAuthStore((state) => state.token)
  const { playVideo, currentVideo, isPlaying } = useVideoPlayerStore()

  const { data: series, isLoading } = useQuery<SeriesDetail>({
    queryKey: ['series', id],
    queryFn: async () => {
      const response = await api.get(`/series/${id}`)
      return response.data
    },
    enabled: !!id,
  })

  // Fetch watched status for all episodes
  useEffect(() => {
    if (series && series.episodes.length > 0) {
      const episodeIds = series.episodes.map(e => e.id)
      videos.getWatchedBatch(episodeIds).then(setWatchedStatus)
    }
  }, [series])

  const refreshMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/series/${id}/refresh`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['series', id] })
      queryClient.invalidateQueries({ queryKey: ['series'] })
    },
  })

  const handleWatchedChange = (episodeId: string, watched: boolean) => {
    setWatchedStatus(prev => ({ ...prev, [episodeId]: watched }))
  }

  const getStreamUrl = (episodeId: string) => {
    if (import.meta.env.DEV) {
      const host = window.location.hostname
      return `http://${host}:4322/api/stream/video/${episodeId}/transcode?token=${token}`
    }
    return `/api/stream/video/${episodeId}/transcode?token=${token}`
  }

  const handlePlayEpisode = (episode: Episode, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    playVideo({
      id: episode.id,
      title: episode.title,
      seriesTitle: series?.title,
      seasonNumber: episode.season_number ?? undefined,
      episodeNumber: episode.episode_number ?? undefined,
      thumbnailPath: episode.thumbnail_path ?? undefined,
      durationMs: episode.duration_ms ?? undefined,
      streamUrl: getStreamUrl(episode.id),
    })
  }

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-dark-400">Loading...</div>
      </div>
    )
  }

  if (!series) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-dark-400">Series not found</div>
      </div>
    )
  }

  // Use all_episodes if available (includes placeholders), otherwise fall back to local episodes
  const hasAllEpisodes = series.all_episodes && series.all_episodes.length > 0

  // Group episodes by season - use all_episodes for full data
  const episodesBySeason = hasAllEpisodes
    ? series.all_episodes.reduce((acc, episode) => {
        const season = episode.season
        if (!acc[season]) {
          acc[season] = []
        }
        acc[season].push(episode)
        return acc
      }, {} as Record<number, EpisodeInfo[]>)
    : series.episodes.reduce((acc, episode) => {
        const season = episode.season_number ?? 0
        if (!acc[season]) {
          acc[season] = []
        }
        // Convert Episode to EpisodeInfo format
        acc[season].push({
          season: episode.season_number ?? 0,
          episode: episode.episode_number ?? 0,
          title: episode.title,
          overview: episode.overview,
          airdate: null,
          thumbnail_url: episode.thumbnail_path,
          is_downloaded: true,
          video_id: episode.id,
        })
        return acc
      }, {} as Record<number, EpisodeInfo[]>)

  // Sort seasons
  const seasons = Object.keys(episodesBySeason)
    .map(Number)
    .sort((a, b) => a - b)

  // Set default active season if not set
  const currentSeason = activeSeason ?? (seasons.length > 0 ? seasons[0] : null)

  // Get local episode by season/episode number
  const getLocalEpisode = (seasonNum: number, epNum: number): Episode | undefined => {
    return series.episodes.find(
      (ep) => ep.season_number === seasonNum && ep.episode_number === epNum
    )
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Back button */}
      <Link
        to="/tv"
        className="inline-flex items-center gap-2 text-dark-400 hover:text-white mb-4 sm:mb-6 text-sm sm:text-base"
      >
        <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
        Back to TV Shows
      </Link>

      {/* Series header - stacks on mobile */}
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="w-32 sm:w-48 mx-auto sm:mx-0 flex-shrink-0">
          <div className="aspect-[2/3] bg-dark-700 rounded-lg flex items-center justify-center overflow-hidden">
            {series.poster_path ? (
              <img
                src={`/api/image/${encodeURIComponent(series.poster_path)}`}
                alt={series.title}
                className="w-full h-full object-cover" loading="lazy"
              />
            ) : (
              <Tv size={48} className="sm:w-16 sm:h-16 text-dark-500" />
            )}
          </div>
        </div>
        <div className="flex-1 text-center sm:text-left">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3 sm:mb-0">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">{series.title}</h1>
              {series.year && (
                <p className="text-dark-400 mb-2 sm:mb-4">{series.year}</p>
              )}
            </div>
            <button
              onClick={() => refreshMutation.mutate()}
              disabled={refreshMutation.isPending}
              className="btn btn-secondary flex items-center gap-2 mx-auto sm:mx-0"
            >
              <RefreshCw size={16} className={refreshMutation.isPending ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">{refreshMutation.isPending ? 'Refreshing...' : 'Refresh Metadata'}</span>
              <span className="sm:hidden">{refreshMutation.isPending ? 'Refreshing...' : 'Refresh'}</span>
            </button>
          </div>
          {series.overview && (
            <p className="text-sm sm:text-base text-dark-300 mb-3 sm:mb-4 line-clamp-3 sm:line-clamp-none">{series.overview}</p>
          )}
          <div className="flex justify-center sm:justify-start gap-4 text-sm text-dark-400">
            <span>{seasons.length} Season{seasons.length !== 1 ? 's' : ''}</span>
            <span>{series.episodes.length} Episode{series.episodes.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* Season tabs and filter toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6 border-b border-dark-700 pb-3 sm:pb-4">
        {/* Season tabs */}
        {(() => {
          // Filter seasons based on download filter
          const filteredSeasons = showOnlyDownloaded
            ? seasons.filter(seasonNum => episodesBySeason[seasonNum].some(ep => ep.is_downloaded))
            : seasons

          return filteredSeasons.length > 1 && (
            <div className="flex gap-2 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
              {filteredSeasons.map((seasonNum) => {
                const episodeCount = showOnlyDownloaded
                  ? episodesBySeason[seasonNum].filter(ep => ep.is_downloaded).length
                  : episodesBySeason[seasonNum].length

                return (
                  <button
                    key={seasonNum}
                    onClick={() => setActiveSeason(seasonNum)}
                    className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium transition-colors whitespace-nowrap ${
                      currentSeason === seasonNum
                        ? 'bg-primary-500 text-white'
                        : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
                    }`}
                  >
                    {seasonNum === 0 ? 'Specials' : `S${seasonNum}`}
                    <span className="ml-1 sm:ml-2 text-xs sm:text-sm opacity-70">
                      ({episodeCount})
                    </span>
                  </button>
                )
              })}
            </div>
          )
        })()}

        {/* Show all / Downloaded only toggle */}
        {hasAllEpisodes && (
          <button
            onClick={() => setShowOnlyDownloaded(!showOnlyDownloaded)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              showOnlyDownloaded
                ? 'bg-primary-500/20 text-primary-400 border border-primary-500/50'
                : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
            }`}
          >
            {showOnlyDownloaded ? (
              <>
                <Eye size={16} />
                <span>Downloaded Only</span>
              </>
            ) : (
              <>
                <EyeOff size={16} />
                <span>Show All</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* Episodes for selected season */}
      {currentSeason !== null && episodesBySeason[currentSeason] && (
        <div>
          {seasons.length === 1 && (
            <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">
              {currentSeason === 0 ? 'Specials' : `Season ${currentSeason}`}
            </h2>
          )}
          <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {episodesBySeason[currentSeason]
              .filter((ep) => !showOnlyDownloaded || ep.is_downloaded)
              .sort((a, b) => a.episode - b.episode)
              .map((ep) => {
                const localEpisode = ep.video_id ? getLocalEpisode(ep.season, ep.episode) : undefined
                const isWatched = ep.video_id ? (watchedStatus[ep.video_id] || false) : false
                const isCurrentlyPlaying = ep.video_id && currentVideo?.id === ep.video_id && isPlaying
                const isAired = !ep.airdate || new Date(ep.airdate) <= new Date()

                // Placeholder episode (not downloaded)
                if (!ep.is_downloaded) {
                  return (
                    <div
                      key={`${ep.season}-${ep.episode}`}
                      className={`card transition-colors overflow-hidden relative ${
                        isAired ? 'opacity-60' : 'opacity-40'
                      }`}
                    >
                      {/* Episode thumbnail placeholder */}
                      <div className="aspect-video bg-dark-800 flex items-center justify-center relative -mx-4 -mt-4 mb-3 sm:mb-4 overflow-hidden">
                        {ep.thumbnail_url ? (
                          <img
                            src={ep.thumbnail_url}
                            alt={ep.title}
                            className="w-full h-full object-cover opacity-50"
                          />
                        ) : (
                          <Tv size={28} className="sm:w-8 sm:h-8 text-dark-600" />
                        )}

                        {/* Episode number badge */}
                        <div className="absolute top-2 left-2 bg-black/70 px-2 py-1 rounded text-xs sm:text-sm font-medium">
                          E{ep.episode.toString().padStart(2, '0')}
                        </div>

                        {/* Not downloaded indicator */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-dark-900/80 px-3 py-2 rounded-lg flex items-center gap-2">
                            {isAired ? (
                              <>
                                <Download size={16} className="text-dark-400" />
                                <span className="text-sm text-dark-400">Not Downloaded</span>
                              </>
                            ) : (
                              <>
                                <Calendar size={16} className="text-dark-400" />
                                <span className="text-sm text-dark-400">
                                  {ep.airdate ? new Date(ep.airdate).toLocaleDateString() : 'TBA'}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Episode info */}
                      <div>
                        <h3 className="text-sm sm:text-base font-medium text-dark-400 line-clamp-1 mb-1">
                          {ep.title}
                        </h3>
                        {ep.overview ? (
                          <p className="text-xs sm:text-sm text-dark-500 line-clamp-2">{ep.overview}</p>
                        ) : (
                          <p className="text-xs sm:text-sm text-dark-600 italic">No description</p>
                        )}
                        {ep.airdate && (
                          <p className="text-xs text-dark-500 mt-2">
                            Air date: {new Date(ep.airdate).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  )
                }

                // Downloaded episode
                return (
                  <Link
                    key={ep.video_id}
                    to={`/tv/${id}/episode/${ep.video_id}`}
                    className="card hover:bg-dark-700 transition-colors cursor-pointer group overflow-hidden relative block"
                  >
                    {/* Episode thumbnail */}
                    <div className="aspect-video bg-dark-700 flex items-center justify-center relative -mx-4 -mt-4 mb-3 sm:mb-4 overflow-hidden">
                      {ep.thumbnail_url ? (
                        <img
                          src={ep.thumbnail_url.startsWith('http') ? ep.thumbnail_url : `/api/image/${encodeURIComponent(ep.thumbnail_url)}`}
                          alt={ep.title}
                          className="w-full h-full object-cover" loading="lazy"
                        />
                      ) : (
                        <Tv size={28} className="sm:w-8 sm:h-8 text-dark-500" />
                      )}

                      {/* Unwatched indicator */}
                      {!isWatched && (
                        <div className="absolute top-0 right-0 w-0 h-0 border-t-[20px] sm:border-t-[24px] border-t-primary-500 border-l-[20px] sm:border-l-[24px] border-l-transparent" />
                      )}

                      {/* Play button overlay */}
                      {localEpisode && (
                        <div
                          onClick={(e) => handlePlayEpisode(localEpisode, e)}
                          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                        >
                          <div className="w-12 h-12 sm:w-14 sm:h-14 bg-primary-500 rounded-full flex items-center justify-center hover:scale-110 transition-transform">
                            <Play size={24} className="sm:w-7 sm:h-7 text-white ml-1" />
                          </div>
                        </div>
                      )}

                      {/* Currently playing indicator */}
                      {isCurrentlyPlaying && (
                        <div className="absolute bottom-2 left-2 bg-primary-500 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                          Playing
                        </div>
                      )}

                      {/* Episode number badge */}
                      <div className="absolute top-2 left-2 bg-black/70 px-2 py-1 rounded text-xs sm:text-sm font-medium">
                        E{ep.episode.toString().padStart(2, '0')}
                      </div>

                      {/* Duration badge */}
                      {localEpisode?.duration_ms && (
                        <div className="absolute bottom-2 right-2 bg-black/70 px-2 py-1 rounded text-xs">
                          {formatDuration(localEpisode.duration_ms)}
                        </div>
                      )}

                      {/* 3-dot menu */}
                      {ep.video_id && (
                        <div
                          className="absolute top-2 right-8 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => e.preventDefault()}
                        >
                          <VideoMenu
                            videoId={ep.video_id}
                            isWatched={isWatched}
                            onWatchedChange={(watched) => handleWatchedChange(ep.video_id!, watched)}
                          />
                        </div>
                      )}
                    </div>

                    {/* Episode info */}
                    <div>
                      <h3 className="text-sm sm:text-base font-medium group-hover:text-primary-400 line-clamp-1 mb-1">
                        {ep.title}
                      </h3>
                      {ep.overview ? (
                        <p className="text-xs sm:text-sm text-dark-400 line-clamp-2">{ep.overview}</p>
                      ) : (
                        <p className="text-xs sm:text-sm text-dark-500 italic">No description</p>
                      )}
                      <div className="flex items-center gap-2 text-xs text-dark-500 mt-2">
                        {localEpisode?.resolution && (
                          <span className="px-1.5 py-0.5 bg-dark-600 rounded">{localEpisode.resolution}</span>
                        )}
                        {localEpisode?.codec && (
                          <span className="px-1.5 py-0.5 bg-dark-600 rounded uppercase">{localEpisode.codec}</span>
                        )}
                      </div>
                    </div>
                  </Link>
                )
              })}
          </div>
        </div>
      )}
    </div>
  )
}
